import {CircularProgress, createStyles, makeStyles, Theme, Typography} from "@material-ui/core";
import Colors from "Constants/Colors";
import { FPSOArea } from "Models/FPSOArea";
import { FC, useCallback, useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DoublePieChart } from "Views/Components/FpsoOverview/Graphs/DoublePieChart";
import {useAuth} from "../../../../auth";
import {CarUtils, RatioResult} from "../../../../Utils/CarUtils";

interface Props {
  readonly fpsoArea: FPSOArea;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    errorContainer: {
      padding: theme.spacing(2),
    },
  }),
);

export const CurrentOpenCloseRatioChart: FC<Props> = ({ fpsoArea }) => {
  const { t } = useTranslation("cars");
  const { auth } = useAuth();
  const classes = useStyles();

  const [chartData, setChartData] = useState<Plotly.Data[]>([]);
  const [carStatusRatios, setCarStatusRatios] = useState<RatioResult[]>([]);
  const [carAnpgActionRequiredRatio, setCarAnpgActionRequiredRatio] = useState<RatioResult[]>([]);
  const [carAuditorActionRequiredRatio, setCarAuditorActionRequiredRatio] = useState<RatioResult[]>([]);
  const [carOperatorActionRequiredRatio, setCarOperatorActionRequiredRatio] = useState<RatioResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getData = useCallback(async () => {
    setLoading(true);
    const token = await auth?.getToken();
    CarUtils.GetRatio(
      "GetCarStatusRatio",
      fpsoArea.id,
      `bearer ${token}`,
    ).then((ratios) => {
      setCarStatusRatios(ratios?.data);
      setLoading(false);
    });

    CarUtils.GetRatio(
      "GetCarAnpgActionRequiredRatio",
      fpsoArea.id,
      `bearer ${token}`,
    ).then((anpgRatios) => {
      setCarAnpgActionRequiredRatio(anpgRatios?.data);
      setLoading(false);
    });

    CarUtils.GetRatio(
      "GetCarAuditorActionRequiredRatio",
      fpsoArea.id,
      `bearer ${token}`,
    ).then((auditorRatios) => {
      setCarAuditorActionRequiredRatio(auditorRatios?.data);
      setLoading(false);
    });

    CarUtils.GetRatio(
      "GetCarOperatorActionRequiredRatio",
      fpsoArea.id,
      `bearer ${token}`,
    ).then((operatorRatios) => {
      setCarOperatorActionRequiredRatio(operatorRatios?.data);
      setLoading(false);
    });

  }, [fpsoArea.id, auth]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [fpsoArea.id]);

  const handleCarRatioDataReceived = useCallback(() => {
    const mainKeys: string[] = [];
    const mainValues: number[] = [];
    const mainSectionColors: string[] = [];

    carStatusRatios.forEach((ratio) => {
      mainSectionColors.push(
        ratio.result === "OPEN"
          ? Colors.STATUS_OPEN
          : ratio.result === "CLOSED"
            ? Colors.STATUS_CLOSED
            : "",
      );
      const newRatio = { ...ratio };

      newRatio.result =
        ratio.result === "OPEN" ||
        ratio.result === "OPEN_EVIDENCE_AWAITING_REVIEW" ||
        ratio.result === "OPEN_EVIDENCE_REQUIRES_OPERATOR_REVISION" ||
        ratio.result === "OPEN_EVIDENCE_REVIEWED"
          ? t("cars:statusOpen")
          : ratio.result === "CLOSED"
            ? t("cars:statusClosed")
            : ratio.result;

      mainKeys.push(newRatio.result);
      mainValues.push(newRatio.ratio);
    });

    const carStatusTrace = {
      values: mainValues,
      labels: mainKeys,
      type: "pie",
      name: "Car status ratio",
      textfont: {
        family: '"Red Hat Display", sans-serif',
        size: 20,
        color: Colors.DARK_GREY,
      },
      textinfo: "value",
      showlegend: true,
      automargin: true,
      sort: false,
      marker: {
        colors: mainSectionColors,
        line: {
          color: Colors.WHITE,
          width: [4, 4, 4, 4, 4],
        },
      },
      hoverinfo: "label+value+percent",
      domain: { column: 0 },
    };

    const secondaryKeys: string[] = [];
    const secondaryValues: number[] = [];
    const secondarySectionColors: string[] = [];

    carAnpgActionRequiredRatio.forEach((anpgRatio) => {
      if (anpgRatio.result === "true") {
        secondarySectionColors.push(
          anpgRatio.result === "true" ? Colors.ANPG_ACTION_REQUIRED : "",
        );
        secondaryKeys.push("Actions Required by ANPG");
        secondaryValues.push(anpgRatio.ratio);
      }
    });

    carAuditorActionRequiredRatio.forEach((auditorRatio) => {
      if (auditorRatio.result === "true") {
        secondarySectionColors.push(
          auditorRatio.result === "true"
            ? Colors.AUDITOR_ACTION_REQUIRED
            : "",
        );
        secondaryKeys.push("Actions Required by OGC");
        secondaryValues.push(auditorRatio.ratio);
      }
    });

    carOperatorActionRequiredRatio.forEach((operatorRatio) => {
      if (operatorRatio.result === "true") {
        secondarySectionColors.push(
          operatorRatio.result === "true"
            ? Colors.OPERATOR_ACTION_REQUIRED
            : "",
        );
        secondaryKeys.push(
          "Actions Required by " + fpsoArea.fpso.block.owner.companyName,
        );
        secondaryValues.push(operatorRatio.ratio);
      }
    });

    const secondaryCarStatusTrace = {
      values: secondaryValues,
      labels: secondaryKeys,
      type: "pie",
      name: "Actions Required:",
      textfont: {
        family: '"Red Hat Display", sans-serif',
        size: 19,
        color: Colors.DARK_GREY,
      },
      textinfo: "value",
      showlegend: true,
      automargin: true,
      sort: false,
      marker: {
        colors: secondarySectionColors,
        line: {
          color: Colors.WHITE,
          width: [4, 4, 4, 4, 4],
        },
      },
      hoverinfo: "label+value",
      domain: { x: [0.45, 1], y: [0.45, 1] },
    };

    const plotData: Plotly.Data[] = [];
    plotData.push(carStatusTrace as Plotly.Data);
    plotData.push(secondaryCarStatusTrace as Plotly.Data);

    setChartData(plotData);

    // eslint-disable-next-line
  }, [carStatusRatios, carAnpgActionRequiredRatio, carOperatorActionRequiredRatio, carAnpgActionRequiredRatio]);

  useEffect(() => {
    handleCarRatioDataReceived();
    // eslint-disable-next-line
  }, [carStatusRatios, carAnpgActionRequiredRatio, carOperatorActionRequiredRatio, carAnpgActionRequiredRatio]);

  const titletext = t("cars:carStatusRatio");
  const subtitletext = t("cars:allCars");
  const subtitlepositionY = 0.9;
  const secondarySubtitleText = t("numActionsRequired");
  const secondarySubtitlePositionY = 0.9;

  if (loading) {
    return (
      <CircularProgress />
    );
  }

  if (carStatusRatios.length !== 0) {
    return (
      <DoublePieChart
        data={chartData}
        titleText={titletext}
        subtitleText={subtitletext}
        subtitlePositionY={subtitlepositionY}
        secondarySubtitleText={secondarySubtitleText}
        secondarySubtitlePositionY={secondarySubtitlePositionY}
      />
    );
  }

  if (carStatusRatios.length === 0) {
    return (
      <div className={classes.errorContainer}>
        <Typography>{t("errors:carStatusRatioChartFetchNoData")}</Typography>
      </div>
    );
  }

  return (
    <div>
      <Typography>{t("errors:carStatusRatioChartFetchError")}</Typography>
    </div>
  );
};
